import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { API } from "@/lib/gp";

function eventDate(value) {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isDateOnly(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export default function Calendar() {
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      fetch(`${API}/tournaments`).then((r) => r.json()).catch(() => null),
      fetch(`${API}/next-event`).then((r) => r.json()).catch(() => null),
    ]).then(([listed, next]) => {
      const fromDb = listed?.tournaments || [];
      const featured = next?.event;
      const merged = [...fromDb];
      if (featured?.start_at && !merged.some((row) => row.id === featured.id)) {
        merged.unshift({
          id: featured.id,
          name: featured.name,
          game: featured.game,
          start_at: featured.start_at,
          format: featured.date_label || featured.type,
          waitlist: true,
        });
      }
      merged.sort((a, b) => (eventDate(a.start_at) || 0) - (eventDate(b.start_at) || 0));
      setRows(merged);
    });
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-5 md:px-8 py-12 md:py-16">
      <div className="flex items-center gap-3 mb-2">
        <CalendarDays className="text-gp-blue" size={26} />
        <h1 className="font-unbounded font-black tracking-tighter text-4xl uppercase">Event Calendar</h1>
      </div>
      <p className="text-white/60 mb-10">Upcoming GamePlat tournaments and events.</p>

      <div className="relative pl-8" data-testid="calendar-list">
        <div className="absolute left-2 top-2 bottom-2 w-px bg-white/10" />
        {rows.length === 0 && (
          <p className="text-white/55 text-sm">No dated events yet. Join the tournament waitlist.</p>
        )}
        {rows.map((t) => {
          const d = eventDate(t.start_at);
          if (!d) return null;
          return (
            <div key={t.id} className="relative mb-6">
              <span className="absolute -left-[26px] top-1.5 h-3 w-3 rounded-full bg-gp-blue ring-4 ring-gp-blue/20" />
              <button onClick={() => navigate(t.waitlist ? "/tournaments" : `/tournaments/${t.id}`)} className="glass w-full text-left rounded-2xl p-5 hover:ring-1 hover:ring-gp-blue/40 transition-all">
                <div className="flex items-center gap-3">
                  <div className="text-center shrink-0">
                    <p className="font-unbounded font-black text-2xl leading-none">{d.getDate()}</p>
                    <p className="text-xs uppercase text-white/50">{d.toLocaleString(undefined, { month: "short" })}</p>
                  </div>
                  <div className="border-l border-white/10 pl-3">
                    <span className="text-[11px] uppercase tracking-widest text-gp-blue">{t.game}</span>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-white/50">
                      {isDateOnly(t.start_at)
                        ? (t.format || "Join the waitlist")
                        : `${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} · ${t.format}`}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
